import { toast } from "react-toastify"

import { REQ } from "constants/api/requests"
import { IResponse, IResponsePayload } from "constants/interfaces"

import useApi from "util/api"

import {
  Paginated,
  RuleFieldsResponse,
  StartSweepResult,
  Tag,
  TagRule,
  TagRuleRun,
  TagSweepStatus,
  TagWithUsage
} from "./type"

class TagsService {
  async getTags(params?: any): Promise<Paginated<Tag> | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<Paginated<Tag>>>(REQ.ADMIN_DASHBOARD.TAGS.LIST, { params })
      if (data?.success) return data.response.payload
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to fetch tags")
    }
  }

  public async getTagsStats(params?: any): Promise<TagWithUsage[] | void> {
    try {
      const { data } = await useApi.get<IResponse<TagWithUsage[]>>(REQ.ADMIN_DASHBOARD.TAGS.STATS, { params })
      if (data?.success) return data.response
    } catch (err: any) {
      toast.error(err?.message ?? err?.data?.message ?? "Failed to get tags stats")
    }
  }

  public async createTag(payload: Partial<Tag>): Promise<Tag | void> {
    try {
      const { data } = await useApi.post<IResponse<Tag>>(REQ.ADMIN_DASHBOARD.TAGS.CREATE, payload)
      if (data?.success) return data.response
    } catch (err: any) {
      toast.error(err?.data?.message ?? err?.message ?? "Failed to create tag")
    }
  }

  public async updateTag(id: string, payload: Partial<Tag>): Promise<Tag | void> {
    try {
      const { data } = await useApi.patch<IResponse<Tag>>(REQ.ADMIN_DASHBOARD.TAGS.UPDATE.replace(":id", id), payload)
      if (data?.success) return data.response
    } catch (err: any) {
      toast.error(err?.data?.message ?? err?.message ?? "Failed to update tag")
    }
  }

  /**
   * `force` detaches the tag from every player holding it and then deletes it. The API refuses
   * without it while the tag is in use, which is the default an operator should hit first.
   */
  public async deleteTag(id: string, options?: { force?: boolean }): Promise<{ result: string; unassigned?: number } | void> {
    try {
      const { data } = await useApi.delete<IResponse<{ result: string; unassigned?: number }>>(
        REQ.ADMIN_DASHBOARD.TAGS.DELETE.replace(":id", id),
        { params: options?.force ? { force: true } : undefined }
      )
      if (data?.success) return data.response
    } catch (err: any) {
      toast.error(err?.data?.message ?? err?.message ?? "Failed to delete tag")
    }
  }

  async getFields(): Promise<RuleFieldsResponse | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<RuleFieldsResponse>>(REQ.ADMIN_DASHBOARD.TAG_RULES.FIELDS)
      if (data?.success) return data.response.payload
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to fetch rule fields")
    }
  }

  async getRules(params?: any): Promise<Paginated<TagRule> | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<Paginated<TagRule>>>(REQ.ADMIN_DASHBOARD.TAG_RULES.LIST, { params })
      if (data?.success) return data.response.payload
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to fetch rules")
    }
  }

  async createRule(payload: any): Promise<TagRule | void> {
    try {
      const { data } = await useApi.post<IResponsePayload<TagRule>>(REQ.ADMIN_DASHBOARD.TAG_RULES.CREATE, payload)
      if (data?.success) return data.response.payload
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to create rule")
    }
  }

  async updateRule(id: string, payload: any): Promise<TagRule | void> {
    try {
      const { data } = await useApi.put<IResponsePayload<TagRule>>(REQ.ADMIN_DASHBOARD.TAG_RULES.UPDATE.replace(":id", id), payload)
      if (data?.success) return data.response.payload
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to update rule")
    }
  }

  async deleteRule(id: string): Promise<{ deleted: boolean; unassigned?: number } | void> {
    try {
      const { data } = await useApi.delete<IResponsePayload<{ deleted: boolean; unassigned?: number }>>(
        REQ.ADMIN_DASHBOARD.TAG_RULES.DELETE.replace(":id", id)
      )
      if (data?.success) return data.response.payload
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to delete rule")
    }
  }

  async runRule(id: string): Promise<StartSweepResult | void> {
    try {
      const { data } = await useApi.post<IResponsePayload<StartSweepResult>>(
        REQ.ADMIN_DASHBOARD.TAG_RULES.RUN_ONE.replace(":id", id)
      )
      if (data?.success) return data.response.payload
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to start rule run")
    }
  }

  async runBatch(): Promise<StartSweepResult | void> {
    try {
      const { data } = await useApi.post<IResponsePayload<StartSweepResult>>(REQ.ADMIN_DASHBOARD.TAG_RULES.RUN_BATCH)
      if (data?.success) return data.response.payload
    } catch (e: any) {
      toast.error(e?.data?.message ?? e?.message ?? "Failed to start batch run")
    }
  }

  async getSweepStatus(): Promise<TagSweepStatus | void> {
    try {
      const { data } = await useApi.get<IResponsePayload<TagSweepStatus>>(REQ.ADMIN_DASHBOARD.TAG_RULES.SWEEP_STATUS)
      if (data?.success) return data.response.payload
    } catch {
      // A status banner must never interrupt the page; the next poll retries.
    }
  }

  async getRuns(params: {
    page?: number
    pageSize?: number
    sortRow?: string
    sortOrder?: "asc" | "desc"
    status?: string
    mode?: string
    ruleId?: string
    q?: string
  }): Promise<Paginated<TagRuleRun>> {
    const res = await useApi.get(REQ.ADMIN_DASHBOARD.TAG_RULES.HISTORY, { params })
    return res.data?.response.payload
  }

  async getRunById(id: string): Promise<TagRuleRun> {
    const res = await useApi.get(REQ.ADMIN_DASHBOARD.TAG_RULES.HISTORY_BY_ID.replace(":id", id))
    return res.data?.payload
  }
}

export const tagsService = new TagsService()
